import React from "react";
import styled from "styled-components";
import { path } from "ramda";
import { PrimaryButton } from "../common/Button";
import UpdateTopNineHandler from "../common/Checkout/UpdateTopNineHandler";
import { withUnlockTopNine } from "../../core/withUnlockTopNine";
import SuccessModal from "../common/SuccessPurchaseModal";
import BuyForm from "../common/Checkout/BuyForm";
import SupportUs from "./SupportUs";
import UnlockTopNineInfo from "./UnlockTopNineInfo";

const getSourceWithWatermark = path(["sources", 0, "withWatermark"]);
const getTopNineOrderId = path(["topNineOrders", 0, "id"]);
const getToken = path(["token"]);

const UnlockTopNine = ({ handleSetStatus, ...props }) => (
  <Container>
    <React.Fragment>
      <SuccessModal open={props.successModalOpen} onClose={props.onRequestClose} />
      <BuyTopNine
        setStatus={handleSetStatus}
        showWatermarkButton={props.withWatermark && props.status !== "success"}
        {...props}
      />
    </React.Fragment>
  </Container>
);

const BuyTopNine = ({
  user,
  setStatus,
  status,
  isLoading,
  onRequestClose,
  paymentRequired,
  withWatermark,
  refreshCurrentUser,
  unlockTopNinePrice,
  watermarkPrice,
  storyUrl,
  showWatermarkButton,
  userName,
  userEmail,
  ...props
}) => (
  <DetailContainer>
    <UpdateTopNineHandler userToken={getToken(user)} id={getTopNineOrderId(user)}>
      {({ updateTopNineOrder, error }) => (
        <React.Fragment>
          <DownloadStory
            storyUrl={storyUrl}
            isLoading={isLoading}
            withWatermark={showWatermarkButton}
            onClickShowPurchaseWatermark={props.onClickShowPurchaseWatermark}
            showPurchaseWatermarkModal={props.showPurchaseWatermarkModal}
            withRedBackground={showWatermarkButton && props.withRedBackground}
            withBlueBackground={props.withBlueBackground || !showWatermarkButton}
            text={showWatermarkButton ? `${props.showEmoji ? "😔" : ""} Download with watermark` : "Download now"}
          />
          {showWatermarkButton && (
            <BuyForm
              {...props}
              withBlueBackground={true}
              showStripeCopy={false}
              error={error}
              price={watermarkPrice}
              isLoading={isLoading}
              buttonText={
                isLoading
                  ? "Getting..."
                  : `${props.showEmoji ? "🤗" : ""} Remove watermark $(${
                      watermarkPrice ? watermarkPrice / 100 : "1.99"
                    })`
              }
              onTokenSuccess={token =>
                updateTopNineOrder({
                  method: "stripe",
                  token: token.id,
                })
                  .then(() => {
                    setStatus("success");
                    refreshCurrentUser();
                    if (props.onClose) {
                      // Close purchase watermarkModal
                      props.onClose();
                    }
                  })
                  .catch(() => {
                    setStatus("error");
                  })
              }
              paymentOnTokenSuccess={(token, method) =>
                updateTopNineOrder({
                  method,
                  token: token.id,
                })
                  .then(() => {
                    setStatus("success");
                    refreshCurrentUser();
                    if (props.onClose) {
                      // Close purchase watermarkModal
                      props.onClose();
                    }
                  })
                  .catch(() => {
                    setStatus("error");
                  })
              }
              onClickButton={() => {
                setStatus("loading");
              }}
              onClickBuy={props.onClickBuy}
            />
          )}

          {showWatermarkButton && !props.noShowInfo && <SupportUs user={user} />}
          {showWatermarkButton && !props.noShowInfo && (
            <UnlockTopNineInfo
              userName={userName}
              userEmail={userEmail}
              loading={isLoading}
              price={watermarkPrice}
              buttonText={
                isLoading ? "Getting..." : `Remove watermark $(${watermarkPrice ? watermarkPrice / 100 : "1.99"})`
              }
              updateTopNineOrder={updateTopNineOrder}
              error={error}
              setStatus={setStatus}
              refreshCurrentUser={refreshCurrentUser}
              onClickBuy={props.onClickBuy}
            />
          )}
        </React.Fragment>
      )}
    </UpdateTopNineHandler>
  </DetailContainer>
);

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  max-height: 100%;
  align-items: center;
  background: white;
  @media (max-width: 640px) {
    max-width: 420px;
    max-width: 100%;
    width: 100vw;
  }
`;

const DetailContainer = styled.div`
  flex: 1;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
`;

const DownloadStory = ({
  storyUrl,
  isLoading,
  text = "Download Now",
  withWatermark,
  withBlueBackground,
  withRedBackground,
  showPurchaseWatermarkModal,
  onClickShowPurchaseWatermark,
}) => (
  <BigCtaContainer
    withWatermark={withWatermark}
    {...(showPurchaseWatermarkModal && withWatermark
      ? { onClick: onClickShowPurchaseWatermark }
      : { href: storyUrl, target: "_blank" })}
  >
    <Cta
      isLoading={isLoading}
      disabled={isLoading}
      style={{
        ...(withBlueBackground ? { backgroundColor: "#448EFF" } : {}),
        ...(withRedBackground ? { backgroundColor: "#f74255" } : {}),
      }}
    >
      {text}
    </Cta>
  </BigCtaContainer>
);

const BigCtaContainer = styled.a`
  display: block;
  width: 100%;
`;

const Cta = styled(PrimaryButton)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: auto;
  opacity: ${({ isLoading }) => (isLoading ? 0.6 : 1)};
`;

export default withUnlockTopNine(UnlockTopNine);
